import { Stack } from '@mui/material';
import { getFullName } from '@provision/formatting-utils';
import { useForm, useWatch } from 'react-hook-form';
import { isNullish } from 'remeda';
import {
  AddClientStep,
  type SavedClient,
} from 'src/components/AddClientModal/add-client-step';
import { FormAutocomplete } from 'src/components/Forms/FormAutocomplete/form-autocomplete';
import { FormRichTextBubble } from 'src/components/Forms/FormRichTextBubble/form-rich-text-bubble';
import { FormTextField } from 'src/components/Forms/FormTextField/form-text-field';
import { GenericForm } from 'src/components/GenericForm/generic-form';
import { ModalStep, useModalStep, useModalSteps } from 'src/components/ModalContainer';
import { Address } from 'src/graphql/entities/address';
import { useBrandOptions } from 'src/graphql/entities/brand';
import { usePartnersOptions } from 'src/graphql/entities/partner/partner';
import {
  GoogleMapsAddressComponentTypes,
  promisifyGetDetails,
  useNotifications,
} from 'src/utils/hooks';
import {
  AddSiteFormFields,
  type ClientOption,
  getAddSiteDefaultValues,
  getClientOptionLabel,
  type NewAddressFormValues,
  statesOptions,
  useSiteFormSearch,
} from './use-new-site-form';
import { type SiteListCacheOptions, useSaveSite } from './use-save-site';

const BRAND_OPTIONS_SORT = { desc: true };
const ADDRESS_PLACEHOLDER = 'Start typing address';

type AddressSuggestion = google.maps.places.AutocompletePrediction & {
  id: string;
  label: string;
};

export interface AddSiteStepProps {
  partnerId?: number | null;
  address?: Address | null;
  client?: { id: string; name: string; email?: string } | null;
  cacheOptions?: SiteListCacheOptions;
  /** Runs right before the request; a standalone modal closes here instead of waiting. */
  onSaving?: () => void;
  onSaved?: (site: Address) => void;
}

/** Create or update a site; pushed as a step, it pops with the saved site. */
export const AddSiteStep = ({
  partnerId,
  address,
  client,
  cacheOptions,
  onSaving,
  onSaved,
}: AddSiteStepProps) => {
  const { popStep } = useModalStep<Address>();
  const { pushStep } = useModalSteps();
  const { showSuccessAlert } = useNotifications();
  const partnerOptions = usePartnersOptions();
  const isUpdate = !!address;

  const formMethods = useForm<NewAddressFormValues>({
    defaultValues: getAddSiteDefaultValues({
      address,
      clients: client
        ? [
            {
              id: client.id,
              label: getClientOptionLabel({
                fullName: client.name,
                primaryEmail: client.email,
              }),
            },
          ]
        : [],
    }),
    mode: 'onTouched',
  });
  const {
    control,
    getValues,
    handleSubmit,
    setValue,
    formState: { isSubmitting },
  } = formMethods;

  const selectedPartner = useWatch({ control, name: AddSiteFormFields.Partner });
  const sitePartnerId = selectedPartner?.id ?? partnerId;
  const brandOptions = useBrandOptions(sitePartnerId, BRAND_OPTIONS_SORT);
  const { users, clients, address1 } = useSiteFormSearch(sitePartnerId);
  const saveSite = useSaveSite({ address, cacheOptions });

  const fillFromSuggestion = async (suggestion: AddressSuggestion) => {
    setValue(
      AddSiteFormFields.Address1,
      suggestion.structured_formatting?.main_text ?? suggestion.label,
      { shouldValidate: true },
    );
    const details = await promisifyGetDetails(suggestion.id);
    details?.address_components?.forEach(({ types, long_name: longName }) => {
      if (types.includes(GoogleMapsAddressComponentTypes.PostalCode)) {
        setValue(AddSiteFormFields.Zip, longName);
      }
      if (types.includes(GoogleMapsAddressComponentTypes.AdministrativeAreaLevel1)) {
        setValue(
          AddSiteFormFields.State,
          statesOptions.find((state) => state.label.startsWith(longName)) ?? null,
        );
      }
      if (types.includes(GoogleMapsAddressComponentTypes.Locality)) {
        setValue(AddSiteFormFields.City, longName, { shouldValidate: true });
      }
    });
  };

  const createClient = async () => {
    if (isNullish(sitePartnerId)) {
      return;
    }
    const created = await pushStep<SavedClient>(<AddClientStep partnerId={sitePartnerId} />);
    if (!created?.clientId) {
      return;
    }
    const createdOption: ClientOption = {
      id: created.clientId,
      label: getClientOptionLabel({
        fullName: getFullName(created),
        primaryEmail: created.primaryEmail?.value ?? undefined,
      }),
    };
    setValue(
      AddSiteFormFields.Clients,
      [...getValues(AddSiteFormFields.Clients), createdOption],
      {
        shouldDirty: true,
      },
    );
  };

  const onSubmit = handleSubmit(async (values) => {
    const currentPartnerId = values[AddSiteFormFields.Partner]?.id ?? partnerId;
    if (isNullish(currentPartnerId)) {
      return;
    }
    onSaving?.();
    const saved = await saveSite(values, currentPartnerId);
    if (!saved) {
      return;
    }
    const site = {
      ...saved,
      brand:
        saved.brand ||
        brandOptions.find(
          (option) => option.id === values[AddSiteFormFields.ClientSegment]?.id,
        )?.data,
    };
    showSuccessAlert(isUpdate ? 'Site was updated' : 'Site was created');
    onSaved?.(site);
    popStep(site);
  });

  const hasPartner = !isNullish(sitePartnerId);

  return (
    <ModalStep
      title={isUpdate ? 'Update site' : 'Create site'}
      testId="add-site-modal"
      actions={[
        { label: 'Cancel', kind: 'secondary', testId: 'add-site-cancel' },
        {
          label: isUpdate ? 'Save' : 'Create site',
          kind: 'primary',
          onClick: onSubmit,
          loading: isSubmitting,
          testId: 'add-site-submit',
        },
      ]}
    >
      <GenericForm<NewAddressFormValues> formMethods={formMethods}>
        {isNullish(partnerId) && (
          <FormAutocomplete
            type="autocomplete"
            name={AddSiteFormFields.Partner}
            label="Partner"
            required="Partner is required"
            showErrorText
            options={partnerOptions}
            data-testid="add-site-partner"
            onChange={() => {
              setValue(AddSiteFormFields.ClientSegment, null);
              setValue(AddSiteFormFields.Clients, []);
            }}
          />
        )}
        <FormAutocomplete
          type="autocomplete"
          name={AddSiteFormFields.Address1}
          label="Address 1"
          required="Address 1 is required"
          showErrorText
          options={address1.options}
          data-testid="add-site-address1"
          placeholder={ADDRESS_PLACEHOLDER}
          searchPlaceholder={ADDRESS_PLACEHOLDER}
          allowCustomValue
          onSearchChange={address1.search}
          onChange={(value) => {
            const picked =
              value && typeof value === 'object' && !Array.isArray(value)
                ? value.id
                : undefined;
            const suggestion = address1.options.find((option) => option.id === picked);
            if (suggestion) {
              void fillFromSuggestion(suggestion);
            }
          }}
        />
        <FormTextField
          type="textfield"
          name={AddSiteFormFields.Address2}
          label="Address 2"
          data-testid="add-site-address2"
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
          <FormTextField
            type="textfield"
            name={AddSiteFormFields.City}
            label="City"
            required="City is required"
            showErrorText
            fullWidth
            data-testid="add-site-city"
          />
          <FormAutocomplete
            type="autocomplete"
            name={AddSiteFormFields.State}
            label="State/Province"
            options={statesOptions}
            data-testid="add-site-state"
          />
          <FormTextField
            type="textfield"
            name={AddSiteFormFields.Zip}
            label="Postal code"
            fullWidth
            data-testid="add-site-postal-code"
          />
        </Stack>
        <FormTextField
          type="textfield"
          name={AddSiteFormFields.BusinessName}
          label="Business name"
          data-testid="add-site-business-name"
        />
        {hasPartner ? (
          <FormAutocomplete
            type="autocomplete"
            name={AddSiteFormFields.ClientSegment}
            label="Client segment"
            required="Client segment is required"
            showErrorText
            options={brandOptions}
            data-testid="add-site-client-segment"
          />
        ) : null}
        {hasPartner && !isUpdate ? (
          <FormAutocomplete
            type="autocomplete"
            name={AddSiteFormFields.Clients}
            label="Clients"
            options={clients.options}
            multiple
            creatable
            onAddNewItem={() => void createClient()}
            onAddNewItemLabel="Create New Client"
            data-testid="add-site-clients"
            loading={clients.loading}
            hasMore={clients.hasMore}
            onLoadMore={clients.loadMore}
            onSearchChange={clients.search}
          />
        ) : null}
        <FormAutocomplete
          type="autocomplete"
          name={AddSiteFormFields.AccountManager}
          label="Account manager"
          options={users.options}
          data-testid="add-site-account-manager"
          loading={users.loading}
          hasMore={users.hasMore}
          onLoadMore={users.loadMore}
          onSearchChange={users.search}
        />
        <FormRichTextBubble
          type="rich-text-bubble"
          name={AddSiteFormFields.OrgNotes}
          label="Site notes"
        />
      </GenericForm>
    </ModalStep>
  );
};
